import { Link, type Path } from 'react-router';
import { formatFloatAsPercent } from '@gonfalon/format/formatFloatAsPercent';
import { formatNumber } from '@gonfalon/format/formatNumber';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Label, Meter, ProgressBar } from '@launchpad-ui/components';
import clsx from 'clsx';

import {
  DEFAULT_USAGE_PRECISION,
  USAGE_ERROR_THRESHOLD,
  USAGE_UNLIMITED,
  USAGE_WARNING_THRESHOLD,
} from '../../../../constants';
import type { UsageTabData } from '../../../../types';
import { AggregationType } from '../../../../types';
import { getUsageDisplayName } from '../../../utils/getUsageDisplayName';
import { getMaxYValue, UsageDetailChart } from '../../charts/UsageDetailChart';

import styles from './TotalUsageCard.module.css';
import baseStyles from './UsageCardBase.module.css';

type TotalUsageCardProps = {
  title: string;
  value: number;
  limit?: number;
  usageData?: UsageTabData;
  detailsRedirectFn?: ({ metric }: { metric: string }) => Path;
  aggregationType?: AggregationType;
  precision?: number;
  hideMarklines?: boolean;
};

export function TotalUsageCard({
  title,
  value,
  limit,
  usageData,
  detailsRedirectFn,
  aggregationType,
  precision = DEFAULT_USAGE_PRECISION,
  hideMarklines = false,
}: TotalUsageCardProps) {
  const { isLoading, isError, tab } = usageData || {};

  if (isLoading || isError) {
    return <UsageCardPlaceholder status={isLoading ? 'loading' : 'error'} />;
  }

  const percentage = getUsagePercentage(value, limit);
  const errorClass = getErrorClass(percentage);
  const formattedLimit = getFormattedLimit(limit);

  return (
    <div className={baseStyles.cardContainer} data-test-id={`usage-card-${tab || 'seats'}`}>
      <div className={baseStyles.cardSummary}>
        <div className={baseStyles.cardHeader}>
          <h3 className={baseStyles.cardTitle}>{title}</h3>
          {tab && detailsRedirectFn && (
            <Link className={styles.viewDetails} to={detailsRedirectFn({ metric: tab })}>
              <Message id="billing.usageCards.viewDetails" />
            </Link>
          )}
        </div>

        <div className={clsx(baseStyles.cardValue, errorClass)} data-test-id="total-usage">
          {formatNumber(value, { maximumFractionDigits: precision })}
        </div>

        {limit !== undefined && (
          <Meter
            className={clsx(baseStyles.cardMeter, errorClass)}
            value={percentage}
            variant="bar"
            valueLabel={formattedLimit}
            data-test-id="usage-progress-bar"
          >
            <Label className={baseStyles.cardMeter}>{formatFloatAsPercent(percentage / 100)}</Label>
          </Meter>
        )}
      </div>

      {usageData && renderChart({ usageData, precision, limit, aggregationType, hideMarklines })}
    </div>
  );
}

type RenderChartProps = {
  usageData: UsageTabData;
  precision: number;
  limit?: number;
  aggregationType?: AggregationType;
  hideMarklines?: boolean;
};

function renderChart({
  usageData,
  precision,
  limit,
  aggregationType = AggregationType.MONTH_TO_DATE,
  hideMarklines = false,
}: RenderChartProps) {
  const { data, tab, timestamps, hasProjection = false, tooltipComponents } = usageData || {};
  if (!data?.data || !timestamps) {
    return null;
  }

  const series = data.data.series ?? [];
  const yAxisMax = getMaxYValue(hasProjection ? [...series, ...(data.data.projectedSeries ?? [])] : series, precision);
  const abbreviatedName = getUsageDisplayName(tab, 'abbreviated');

  return (
    <>
      <div className={styles.divider} />
      <div className={styles.chartWrapper}>
        <UsageDetailChart
          size="small"
          data={data.data}
          aggregationType={aggregationType}
          abbreviatedName={abbreviatedName}
          fromTimestampUTC={timestamps.from}
          toTimestampUTC={timestamps.to}
          cutoffTimestampUTC={timestamps.cutoff}
          limitValue={limit}
          precision={precision}
          yAxisMax={yAxisMax}
          hideMarklines={hideMarklines}
          showProjection={hasProjection}
          tooltipComponents={tooltipComponents}
        />
      </div>
    </>
  );
}

type UsageCardPlaceholderProps = {
  status: 'loading' | 'error';
};

function UsageCardPlaceholder({ status }: UsageCardPlaceholderProps) {
  return (
    <div className={baseStyles.cardContainer}>
      <div className={baseStyles.cardSummary}>
        <div className={baseStyles.cardHeader}>
          <h3 className={baseStyles.cardTitle}>
            {status === 'loading' ? (
              <ProgressBar aria-label={msg('billing.status.loading')} isIndeterminate />
            ) : (
              <Alert status="error" variant="inline">
                <Message id="billing.usageCards.errorLoadingUsage" />
              </Alert>
            )}
          </h3>
        </div>
        <div className={baseStyles.cardValue} />
        <div className={baseStyles.cardMeter} />
      </div>
      <div className={styles.chartWrapper} />
    </div>
  );
}

function getUsagePercentage(value: number, limit?: number) {
  if (limit === undefined || limit === USAGE_UNLIMITED || limit === 0) {
    return 0;
  }
  return Math.floor((value / limit) * 100);
}

function getErrorClass(percentage: number) {
  if (percentage >= USAGE_ERROR_THRESHOLD) {
    return baseStyles.error;
  }
  if (percentage >= USAGE_WARNING_THRESHOLD) {
    return baseStyles.warning;
  }
  return undefined;
}

function getFormattedLimit(limit?: number): string {
  if (limit === undefined) {
    return '';
  }
  return limit === USAGE_UNLIMITED
    ? msg('billing.usageCards.unlimited')
    : msg('billing.usageCards.limit', { limit: formatNumber(limit) });
}
